import ApiWithLogging from '../utils/apiWithLogging';

// ข้อมูลผู้ใช้แบบย่อ
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// สินค้า/ระบบแบบย่อ (ผูกกับ maintenance type ได้หลายรายการ)
export interface ProductBrief {
  id: number;
  code: string;
  name: string;
}

// Interface สำหรับ MaintenanceType
export interface MaintenanceType {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_active: boolean;
  // สินค้า/ระบบที่ผูกไว้ — [] = "ทั่วไป" (ใช้ได้ทุกสินค้า)
  products: ProductBrief[];
  created_at: string;
  updated_at?: string;
  created_by_id?: number;
  created_by?: UserInfo;
  updated_by_id?: number;
  updated_by?: UserInfo;
}

// Interface สำหรับ MaintenanceType Form
export interface MaintenanceTypeForm {
  code: string;
  name: string;
  description?: string;
  is_active?: boolean;
  // [] = ทั่วไป (ใช้ได้ทุกสินค้า)
  product_ids: number[];
}

// Interface สำหรับ MaintenanceType List Response
export interface MaintenanceTypeListResponse {
  items: MaintenanceType[];
  total: number;
  page: number;
  limit: number;
}

// Interface สำหรับ MaintenanceType Query Parameters
export interface GetMaintenanceTypesParams {
  page?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
}

const maintenanceTypeService = {
  // ดึงรายการประเภทการซ่อมบำรุงทั้งหมด
  async getMaintenanceTypes(params?: GetMaintenanceTypesParams): Promise<MaintenanceTypeListResponse> {
    try {
      const response = await ApiWithLogging.get('/maintenance-types/',
        { params },
        {
          service: 'maintenanceTypeService',
          action: 'view_maintenance_types_list',
          entityType: 'maintenance_type'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching maintenance types:', error);
      throw error;
    }
  },

  // ดึงรายการประเภทการซ่อมบำรุงที่ใช้งานอยู่ (สำหรับ dropdown)
  // ถ้าระบุ productId จะคืนประเภทของสินค้านั้น + กลุ่ม "ทั่วไป"; ถ้าไม่ระบุคืนทุกประเภท
  async getActiveMaintenanceTypes(productId?: number | null): Promise<MaintenanceType[]> {
    try {
      const params = (productId !== undefined && productId !== null)
        ? { product_id: productId }
        : undefined;
      const response = await ApiWithLogging.get('/maintenance-types/active',
        params ? { params } : {},
        {
          service: 'maintenanceTypeService',
          action: 'view_active_maintenance_types',
          entityType: 'maintenance_type'
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching active maintenance types:', error);
      throw error;
    }
  },

  // ดึงข้อมูลประเภทการซ่อมบำรุงตาม ID
  async getMaintenanceTypeById(id: number): Promise<MaintenanceType> {
    try {
      const response = await ApiWithLogging.get(`/maintenance-types/${id}`, {},
        {
          service: 'maintenanceTypeService',
          action: 'view_maintenance_type_detail',
          entityType: 'maintenance_type',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching maintenance type by ID:', error);
      throw error;
    }
  },

  // สร้างประเภทการซ่อมบำรุงใหม่
  async createMaintenanceType(data: MaintenanceTypeForm): Promise<MaintenanceType> {
    try {
      const response = await ApiWithLogging.post('/maintenance-types/', data, {},
        {
          service: 'maintenanceTypeService',
          action: 'create_maintenance_type',
          entityType: 'maintenance_type',
          details: { code: data.code, name: data.name }
        }
      );
      return response;
    } catch (error) {
      console.error('Error creating maintenance type:', error);
      throw error;
    }
  },

  // แก้ไขข้อมูลประเภทการซ่อมบำรุง
  // BUG-S162-P1: pass the record's last-seen updated_at as `ifMatch` to enable the backend
  // optimistic-concurrency lock (stale two-tab overwrite → 409). Optional.
  async updateMaintenanceType(id: number, data: Partial<MaintenanceTypeForm>, ifMatch?: string): Promise<MaintenanceType> {
    try {
      const response = await ApiWithLogging.put(`/maintenance-types/${id}`, data, ifMatch ? { headers: { 'If-Match': ifMatch } } : {},
        {
          service: 'maintenanceTypeService',
          action: 'update_maintenance_type',
          entityType: 'maintenance_type',
          entityId: id,
          details: { updated_fields: Object.keys(data) }
        }
      );
      return response;
    } catch (error) {
      console.error('Error updating maintenance type:', error);
      throw error;
    }
  },

  // ลบประเภทการซ่อมบำรุง
  async deleteMaintenanceType(id: number): Promise<void> {
    try {
      await ApiWithLogging.delete(`/maintenance-types/${id}`, {},
        {
          service: 'maintenanceTypeService',
          action: 'delete_maintenance_type',
          entityType: 'maintenance_type',
          entityId: id
        }
      );
    } catch (error) {
      console.error('Error deleting maintenance type:', error);
      throw error;
    }
  },

  // เปิด/ปิดใช้งานประเภทการซ่อมบำรุง
  async toggleMaintenanceTypeStatus(id: number): Promise<MaintenanceType> {
    try {
      const response = await ApiWithLogging.patch(`/maintenance-types/${id}/toggle`, {}, {},
        {
          service: 'maintenanceTypeService',
          action: 'toggle_maintenance_type_status',
          entityType: 'maintenance_type',
          entityId: id
        }
      );
      return response;
    } catch (error) {
      console.error('Error toggling maintenance type status:', error);
      throw error;
    }
  },

  // ส่งออกข้อมูลประเภทการซ่อมเป็น Excel
  // ส่งออก Excel — ต้องส่งตัวกรองที่หน้าจอใช้อยู่ไปด้วย ไม่งั้นผู้ใช้กรองแล้วกดส่งออก
  // จะได้ไฟล์ทั้งระบบโดยไม่มีอะไรเตือน (ไฟล์ที่ได้ต้องตรงกับที่เห็นบนจอ)
  async exportToExcel(params?: { search?: string; is_active?: boolean }): Promise<Blob> {
    const query: Record<string, string> = {};
    if (params?.search) query.search = params.search;
    if (params?.is_active !== undefined) query.is_active = String(params.is_active);

    const response = await ApiWithLogging.get('/maintenance-types/export/excel',
      {
        responseType: 'blob',
        params: query
      },
      {
        service: 'maintenanceTypeService',
        action: 'export_maintenance_types_excel',
        entityType: 'maintenance_type'
      }
    );
    return response;
  }
};

export default maintenanceTypeService;
